import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Palette, Calculator, Globe, Music, Dumbbell } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';

const MontessoriCurriculum = () => {
  const subjects = [
    { icon: BookOpen, name: 'Language & Literacy', description: 'Reading, writing, phonics, and communication skills development.' },
    { icon: Calculator, name: 'Mathematics', description: 'Number sense, problem-solving, and mathematical concepts through hands-on materials.' },
    { icon: Globe, name: 'Cultural Studies', description: 'Geography, history, science, and understanding of the world around us.' },
    { icon: Palette, name: 'Creative Arts', description: 'Art, craft, drama, and creative expression activities.' },
    { icon: Music, name: 'Music & Movement', description: 'Rhythm, songs, dance, and musical appreciation.' },
    { icon: Dumbbell, name: 'Physical Education', description: 'Motor skills development, sports, and outdoor play.' },
  ];

  return (
    <>
      <PageHeader
        title="Curriculum"
        subtitle="A balanced approach to early childhood education"
        breadcrumbs={[
          { label: 'Our Schools', href: '/our-schools' },
          { label: 'Montessori & Primary', href: '/montessori' },
          { label: 'Curriculum' },
        ]}
        backgroundImage="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1600&h=400&fit=crop"
      />

      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-6">
              Our Educational Approach
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              We blend the Montessori method with the Nigerian curriculum and Early Years Foundation
              Stage (EYFS) to provide a comprehensive education that prepares children for academic
              success while nurturing their natural love of learning.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {subjects.map((subject, index) => {
              const Icon = subject.icon;
              return (
                <motion.div
                  key={subject.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 p-6 rounded-2xl hover:bg-emerald-50 transition-colors"
                >
                  <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="text-emerald-600" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{subject.name}</h3>
                  <p className="text-gray-600">{subject.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-6">
                The Montessori Method
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  The Montessori method is based on the principle that children learn best through
                  hands-on experience and self-directed activity. Our classrooms are designed as
                  prepared environments where children can explore and discover at their own pace.
                </p>
                <p>
                  Key principles include:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                    Mixed-age classrooms that encourage peer learning
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                    Specially designed learning materials
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                    Extended work periods for deep concentration
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                    Freedom of choice within structured limits
                  </li>
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1587654780291-39c9404d746b?w=600&h=500&fit=crop"
                alt="Montessori classroom"
                className="w-full h-[400px] object-cover rounded-2xl shadow-hard"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default MontessoriCurriculum;
